import { ScrollArea as ScrollAreaPrimitive } from '@base-ui/react/scroll-area';
import { t } from '@lingui/core/macro';
import {
  type ColumnOrderState,
  type ColumnFiltersState,
  type ColumnPinningState,
  type SortingState,
  useTable,
} from '@tanstack/react-table';
import { Fragment, type HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';

import ChevronDownIcon from '@/assets/svg/bx-chevron-down.svg?react';
import ChevronRightIcon from '@/assets/svg/bx-chevron-right.svg?react';
import { Button } from '@/components/buttons';
import { SpinnerOverlay } from '@/components/loaders';
import { ScrollBar } from '@/components/ScrollArea';
import { Skeleton } from '@/components/Skeleton';
import { Table, TableBody, TableCell, TableFooter, TableRow } from '@/components/Table';
import { cn } from '@/lib/utils';
import { useOptionalTableState } from '@/providers/TableState';

import { DATA_TABLE_DEFAULT_PAGE_SIZE } from './constants';
import { getPinnedCellClassName, getPinnedCellStyles } from './dataTableCellStyles';
import DataTableExpandedRow from './DataTableExpandedRow';
import { dataTableFeatures } from './dataTableFeatures';
import DataTablePagination from './DataTablePagination';
import DataTableSelectionControl from './DataTableSelectionControl';
import DataTableTableBodyCell from './DataTableTableBodyCell';
import DataTableTableFooterCell from './DataTableTableFooterCell';
import DataTableTableHead from './DataTableTableHead';
import { type DataTableColumnDef, type DataTableProps } from './types';
import useDataTableColumnWidths, { computePinnedOffsets } from './useDataTableColumnWidths';
import useDataTablePagination from './useDataTablePagination';
import useDataTableRowExpansion, { rowHasAncestorInSet } from './useDataTableRowExpansion';
import useDataTableRowSelection from './useDataTableRowSelection';
import useDataTableScrollDirections from './useDataTableScrollDirections';
import useDataTableStickyHeaderScrollSync from './useDataTableStickyHeaderScrollSync';
import useDataTableVirtualScrollbar from './useDataTableVirtualScrollbar';

const DEFAULT_LOADING_ROW_COUNT = 5;

const SELECT_COLUMN_ID = '__select';
const EXPAND_COLUMN_ID = '__expand';

const getColumnId = <TData extends object>(column: DataTableColumnDef<TData>) => {
  if (column.id != null) {
    return column.id;
  }

  if ('accessorKey' in column && column.accessorKey != null) {
    return String(column.accessorKey);
  }

  return undefined;
};

const collectColumnFilters = <TData extends object>(
  columns: DataTableColumnDef<TData>[],
  options: {
    includeDefaultValues: boolean;
  },
) => {
  const collected: ColumnFiltersState = [];
  let hasControlledColumnFilters = false;

  const visitColumn = (column: DataTableColumnDef<TData>) => {
    const columnId = getColumnId(column);

    if ('filteredValue' in column && column.filteredValue !== undefined) {
      hasControlledColumnFilters = true;
      if (columnId != null && column.filteredValue != null) {
        collected.push({
          id: columnId,
          value: column.filteredValue,
        });
      }
    } else if (options.includeDefaultValues && 'defaultFilteredValue' in column) {
      if (columnId != null && column.defaultFilteredValue != null) {
        collected.push({
          id: columnId,
          value: column.defaultFilteredValue,
        });
      }
    }

    if ('columns' in column && Array.isArray(column.columns)) {
      column.columns.forEach((childColumn) =>
        visitColumn(childColumn as DataTableColumnDef<TData>),
      );
    }
  };

  columns.forEach((column) => visitColumn(column));

  return {
    collected,
    hasControlledColumnFilters,
  };
};

const filterHiddenColumns = <TData extends object>(
  columns: DataTableColumnDef<TData>[],
): DataTableColumnDef<TData>[] => {
  const filtered: DataTableColumnDef<TData>[] = [];

  columns.forEach((column) => {
    if (column.hidden === true) {
      return;
    }

    const hasChildColumns = 'columns' in column && Array.isArray(column.columns);
    if (!hasChildColumns) {
      filtered.push(column);
      return;
    }

    const visibleChildren = filterHiddenColumns(column.columns as DataTableColumnDef<TData>[]);
    if (visibleChildren.length === 0) {
      return;
    }

    filtered.push({
      ...column,
      columns: visibleChildren,
    });
  });

  return filtered;
};

const collectLeafColumnIds = <TData extends object>(
  columns: DataTableColumnDef<TData>[],
): string[] => {
  const collected: string[] = [];

  const visit = (column: DataTableColumnDef<TData>) => {
    if ('columns' in column && Array.isArray(column.columns) && column.columns.length > 0) {
      column.columns.forEach((childColumn) => visit(childColumn as DataTableColumnDef<TData>));
      return;
    }

    const columnId = getColumnId(column);
    if (columnId != null) {
      collected.push(columnId);
    }
  };

  columns.forEach((column) => visit(column));
  return collected;
};

/**
 * Numeric columns default to TanStack's `inNumberRange` filterFn, whose `autoRemove` strips any
 * value that is not a `[min, max]` tuple. Custom header filters (e.g. InstantSearch range objects)
 * must use a different `filterFn` or `setFilterValue` is dropped before `onColumnFiltersChange`.
 */
const withDefaultManualFilterFn = <TData extends object>(
  columns: DataTableColumnDef<TData>[],
  manualFiltering: boolean,
): DataTableColumnDef<TData>[] => {
  if (!manualFiltering) {
    return columns;
  }

  return columns.map((column) => {
    if ('columns' in column && Array.isArray(column.columns) && column.columns.length > 0) {
      return {
        ...column,
        columns: withDefaultManualFilterFn(
          column.columns as DataTableColumnDef<TData>[],
          manualFiltering,
        ),
      };
    }

    if (column.filterComponent != null && column.filterFn == null) {
      return {
        ...column,
        filterFn: () => true,
      };
    }

    return column;
  });
};

const applyColumnVisibility = <TData extends object>(
  columns: DataTableColumnDef<TData>[],
  columnVisibility: Record<string, boolean> | undefined,
): DataTableColumnDef<TData>[] => {
  if (columnVisibility == null || Object.keys(columnVisibility).length === 0) {
    return columns;
  }

  const filterColumn = (column: DataTableColumnDef<TData>): DataTableColumnDef<TData> | null => {
    const columnId = getColumnId(column);
    if (columnId != null && columnVisibility[columnId] === false) {
      return null;
    }

    if ('columns' in column && Array.isArray(column.columns)) {
      const visibleChildren = column.columns
        .map((childColumn) => filterColumn(childColumn as DataTableColumnDef<TData>))
        .filter((childColumn): childColumn is DataTableColumnDef<TData> => childColumn != null);

      if (visibleChildren.length === 0) {
        return null;
      }

      return {
        ...column,
        columns: visibleChildren,
      };
    }

    return column;
  };

  return columns
    .map((column) => filterColumn(column))
    .filter((column): column is DataTableColumnDef<TData> => column != null);
};

function DataTable<TData extends object>({
  columns,
  data,
  showHeader = true,
  stickyHeader = true,
  loading = false,
  error,
  emptyMessage = t`No results found.`,
  errorMessage = t`Something went wrong while loading this table.`,
  loadingRowCount = DEFAULT_LOADING_ROW_COUNT,
  size = 'large',
  className,
  tableClassName,
  pagination = { pageSize: DATA_TABLE_DEFAULT_PAGE_SIZE },
  rowCount,
  getRowId,
  defaultColumn,
  enableColumnPinning = true,
  initialColumnPinning,
  manualPagination = false,
  defaultSorting,
  sorting,
  onSortingChange,
  manualSorting = false,
  defaultColumnFilters,
  columnFilters,
  onColumnFiltersChange,
  manualFiltering = false,
  defaultColumnOrder,
  columnOrder,
  onColumnOrderChange,
  defaultColumnVisibility,
  columnVisibility,
  onColumnVisibilityChange,
  rowSelection,
  expandable,
  rowCursor,
  onRow,
  actionContent,
}: DataTableProps<TData>) {
  const tableState = useOptionalTableState();
  const resolvedColumnOrder = columnOrder ?? tableState?.columnOrder;
  const resolvedOnColumnOrderChange = onColumnOrderChange ?? tableState?.onColumnOrderChange;
  const resolvedColumnVisibility = columnVisibility ?? tableState?.columnVisibility;
  const resolvedOnColumnVisibilityChange =
    onColumnVisibilityChange ?? tableState?.onColumnVisibilityChange;

  const { paginationEnabled, resolvedPaginationState, onPaginationChange } =
    useDataTablePagination(pagination);

  const { tableScrollWrapperRef, canScrollLeft, canScrollRight } = useDataTableScrollDirections();
  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const {
    showVirtualScrollbar,
    virtualScrollbarLeft,
    virtualScrollbarWidth,
    virtualScrollbarContentWidth,
    virtualScrollbarViewportRef,
    onVirtualScrollbarScroll,
  } = useDataTableVirtualScrollbar(tableScrollWrapperRef);
  useDataTableStickyHeaderScrollSync(
    tableScrollWrapperRef,
    headerWrapperRef,
    stickyHeader && showHeader,
  );

  const {
    selectionEnabled,
    selectionType,
    selectionColumnWidth,
    selectAllEnabled,
    resolvedSelectedRowIdSet,
    rowSelectionStateForTable,
    onRowSelectionChange,
    resolvedGetRowCanSelect,
    enableMultiRowSelection,
    enableSubRowSelection,
  } = useDataTableRowSelection(rowSelection);

  const {
    expandableEnabled,
    detailExpandable,
    subRowsExpandableMode,
    expansionAnimationDurationMs,
    resolvedExpandedRowIdSet,
    collapsingExpandedRowIdSet,
    expandedStateForTable,
    onExpandedChange,
    resolvedGetSubRows,
    resolvedGetRowCanExpand,
  } = useDataTableRowExpansion(expandable);

  // Sub-row cells mounted during the very first render of the table should not play an
  // enter animation; only cells mounted from subsequent expansions should. A ref is enough
  // because the re-render that reveals newly-expanded sub-rows is already driven by the
  // expanded-state change, and this ref is read during that render.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    hasMountedRef.current = true;
  }, []);

  const visibleColumns = useMemo(() => filterHiddenColumns(columns), [columns]);

  const { collected: defaultColumnFiltersFromColumns } = useMemo(
    () => collectColumnFilters(visibleColumns, { includeDefaultValues: true }),
    [visibleColumns],
  );
  const { collected: controlledColumnFiltersFromColumns, hasControlledColumnFilters } = useMemo(
    () => collectColumnFilters(visibleColumns, { includeDefaultValues: false }),
    [visibleColumns],
  );
  const resolvedDefaultColumnFilters = defaultColumnFilters ?? defaultColumnFiltersFromColumns;
  const resolvedColumnFilters =
    columnFilters ?? (hasControlledColumnFilters ? controlledColumnFiltersFromColumns : undefined);
  const disableSelectAllControl = loading && data.length === 0;
  const selectionColumn = useMemo<DataTableColumnDef<TData> | null>(() => {
    if (!selectionEnabled) {
      return null;
    }

    return {
      id: SELECT_COLUMN_ID,
      size: selectionColumnWidth,
      meta: {
        align: 'center',
      },
      enableSorting: false,
      enablePinning: false,
      header: ({ table }) => {
        if (!selectAllEnabled) {
          return null;
        }

        const selectableRowsOnPage = table.getRowModel().rows.filter((row) => row.getCanSelect());
        const selectedRowsOnPageCount = selectableRowsOnPage.reduce(
          (count, row) => (resolvedSelectedRowIdSet.has(row.id) ? count + 1 : count),
          0,
        );
        const allRowsSelected =
          selectableRowsOnPage.length > 0 &&
          selectedRowsOnPageCount === selectableRowsOnPage.length;
        const someRowsSelected =
          selectedRowsOnPageCount > 0 && selectedRowsOnPageCount < selectableRowsOnPage.length;

        return (
          <div className="flex justify-center">
            <DataTableSelectionControl
              type="checkbox"
              checked={allRowsSelected}
              indeterminate={someRowsSelected}
              disabled={disableSelectAllControl || selectableRowsOnPage.length === 0}
              ariaLabel={allRowsSelected ? t`Deselect all rows` : t`Select all rows`}
              onChange={(checked) =>
                onRowSelectionChange((currentRowSelectionState) => {
                  if (checked) {
                    const nextRowSelectionState = { ...currentRowSelectionState };
                    selectableRowsOnPage.forEach((row) => {
                      nextRowSelectionState[row.id] = true;
                    });
                    return nextRowSelectionState;
                  }

                  const deselectedRowIds = new Set(selectableRowsOnPage.map((row) => row.id));
                  return Object.fromEntries(
                    Object.entries(currentRowSelectionState).filter(
                      ([rowId]) => !deselectedRowIds.has(rowId),
                    ),
                  ) as typeof currentRowSelectionState;
                })
              }
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const selected = resolvedSelectedRowIdSet.has(row.id);

        return (
          <div className="flex justify-center">
            <DataTableSelectionControl
              type={selectionType}
              checked={selected}
              indeterminate={selectionType === 'checkbox' && !selected && row.getIsSomeSelected()}
              disabled={!row.getCanSelect()}
              ariaLabel={
                selectionType === 'radio'
                  ? selected
                    ? t`Selected row`
                    : t`Select row`
                  : selected
                    ? t`Deselect row`
                    : t`Select row`
              }
              onChange={(checked) => {
                if (selectionType === 'radio') {
                  if (!selected) {
                    row.toggleSelected(true);
                  }
                  return;
                }

                row.toggleSelected(checked);
              }}
            />
          </div>
        );
      },
    };
  }, [
    resolvedSelectedRowIdSet,
    onRowSelectionChange,
    disableSelectAllControl,
    selectAllEnabled,
    selectionColumnWidth,
    selectionEnabled,
    selectionType,
  ]);
  const expandableColumn = useMemo<DataTableColumnDef<TData> | null>(() => {
    if (!expandableEnabled || expandable == null) {
      return null;
    }

    return {
      id: EXPAND_COLUMN_ID,
      accessorFn: () => null,
      size: expandable.expandColumnWidth ?? 36,
      meta: {
        align: 'center',
      },
      enableSorting: false,
      enablePinning: true,
      cellProps: ({ row }) => {
        if (!subRowsExpandableMode || row.depth === 0) {
          return undefined;
        }

        const parentRow = row.getParentRow();
        if (!parentRow || parentRow.subRows.length === 0) {
          return undefined;
        }

        if (parentRow.subRows[0]?.id === row.id) {
          return {
            rowSpan: parentRow.subRows.length,
            className: 'align-top bg-white! hover:bg-white! group-hover:bg-white!',
          };
        }

        return {
          rowSpan: 0,
        };
      },
      header: () => null,
      cell: ({ row }) => {
        if (!row.getCanExpand()) {
          return null;
        }

        const expanded = resolvedExpandedRowIdSet.has(row.id);
        return (
          <Button
            variant="ghost"
            size="icon-small"
            aria-label={expanded ? t`Collapse` : t`Expand`}
            aria-expanded={expanded}
            onClick={(event) => {
              event.stopPropagation();
              row.toggleExpanded(!expanded);
            }}
          >
            {expanded ? <ChevronDownIcon aria-hidden /> : <ChevronRightIcon aria-hidden />}
          </Button>
        );
      },
    };
  }, [expandable, expandableEnabled, resolvedExpandedRowIdSet, subRowsExpandableMode]);

  const resolvedColumns = useMemo(() => {
    return [
      selectionColumn,
      expandableColumn,
      ...withDefaultManualFilterFn(visibleColumns, manualFiltering),
    ].filter((column): column is DataTableColumnDef<TData> => column != null);
  }, [visibleColumns, expandableColumn, selectionColumn, manualFiltering]);
  const sizingColumns = useMemo(
    () => applyColumnVisibility(resolvedColumns, resolvedColumnVisibility),
    [resolvedColumns, resolvedColumnVisibility],
  );

  const resolvedColumnIds = useMemo(() => collectLeafColumnIds(resolvedColumns), [resolvedColumns]);

  const resolvedInitialColumnPinning = useMemo<ColumnPinningState | undefined>(() => {
    if (initialColumnPinning == null) {
      return undefined;
    }

    const startPinnedColumnIds = initialColumnPinning.start ?? [];
    const endPinnedColumnIds = initialColumnPinning.end ?? [];

    if (!selectionEnabled) {
      return {
        start: startPinnedColumnIds,
        end: endPinnedColumnIds,
      };
    }

    if (startPinnedColumnIds.length === 0) {
      return {
        start: startPinnedColumnIds,
        end: endPinnedColumnIds.filter((columnId) => columnId !== SELECT_COLUMN_ID),
      };
    }

    const normalizedStartPinnedColumnIds = [
      SELECT_COLUMN_ID,
      ...startPinnedColumnIds.filter((columnId) => columnId !== SELECT_COLUMN_ID),
    ];
    const normalizedEndPinnedColumnIds = endPinnedColumnIds.filter(
      (columnId) => columnId !== SELECT_COLUMN_ID,
    );

    return {
      start: normalizedStartPinnedColumnIds,
      end: normalizedEndPinnedColumnIds,
    };
  }, [initialColumnPinning, selectionEnabled]);

  // Reconcile `resolvedColumnOrder` (which may reference hidden columns or carry stale
  // ids from `localStorage`) against the leaf ids that are actually rendered. Hidden ids
  // and unknown ids are dropped, fixed prefix columns (selection/expand) stay first,
  // and any leaf that's missing from the saved order is appended in declaration order
  // so TanStack always receives a complete, in-bounds `columnOrder`.
  const tableColumnOrder = useMemo<ColumnOrderState | undefined>(() => {
    if (resolvedColumnOrder == null) {
      return undefined;
    }

    const resolvedColumnIdSet = new Set(resolvedColumnIds);
    const fixedPrefix = [
      ...(selectionEnabled ? [SELECT_COLUMN_ID] : []),
      ...(expandableEnabled ? [EXPAND_COLUMN_ID] : []),
    ];

    const orderedIds: string[] = [];
    const seen = new Set<string>();

    fixedPrefix.forEach((columnId) => {
      if (resolvedColumnIdSet.has(columnId) && !seen.has(columnId)) {
        orderedIds.push(columnId);
        seen.add(columnId);
      }
    });

    resolvedColumnOrder.forEach((columnId) => {
      if (resolvedColumnIdSet.has(columnId) && !seen.has(columnId)) {
        orderedIds.push(columnId);
        seen.add(columnId);
      }
    });

    resolvedColumnIds.forEach((columnId) => {
      if (!seen.has(columnId)) {
        orderedIds.push(columnId);
        seen.add(columnId);
      }
    });

    return orderedIds;
  }, [expandableEnabled, resolvedColumnIds, resolvedColumnOrder, selectionEnabled]);

  // Run the flex-style allocator against the measured wrapper width. Output drives the
  // `<col>` widths and feeds TanStack's `state.columnSizing` so `column.getSize()`
  // reflects the post-layout pixels. Pinning offsets are computed separately from the
  // same widths (see `pinnedOffsets` below) instead of going through TanStack's
  // `getStart`/`getAfter` helpers, which can desync from React commits. The allocator
  // keys its output by column id, so it is intentionally agnostic to the visual
  // `columnOrder` — TanStack handles ordering, this hook handles width assignment.
  const { widths: allocatedColumnWidths } = useDataTableColumnWidths(
    tableScrollWrapperRef,
    sizingColumns,
  );

  const [initialState] = useState(() => ({
    ...(resolvedInitialColumnPinning ? { columnPinning: resolvedInitialColumnPinning } : {}),
    ...(sorting == null && defaultSorting ? { sorting: defaultSorting } : {}),
    ...(resolvedColumnFilters == null && resolvedDefaultColumnFilters.length > 0
      ? { columnFilters: resolvedDefaultColumnFilters }
      : {}),
    ...(tableColumnOrder == null && defaultColumnOrder ? { columnOrder: defaultColumnOrder } : {}),
    ...(resolvedColumnVisibility == null &&
    defaultColumnVisibility &&
    Object.keys(defaultColumnVisibility).length > 0
      ? { columnVisibility: defaultColumnVisibility }
      : {}),
  }));

  const state = {
    columnSizing: allocatedColumnWidths,
    ...(paginationEnabled && resolvedPaginationState != null
      ? { pagination: resolvedPaginationState }
      : {}),
    ...(sorting != null ? { sorting } : {}),
    ...(resolvedColumnFilters != null ? { columnFilters: resolvedColumnFilters } : {}),
    ...(tableColumnOrder != null ? { columnOrder: tableColumnOrder } : {}),
    ...(resolvedColumnVisibility != null ? { columnVisibility: resolvedColumnVisibility } : {}),
    ...(selectionEnabled ? { rowSelection: rowSelectionStateForTable } : {}),
    ...(expandableEnabled ? { expanded: expandedStateForTable } : {}),
  };

  const table = useTable({
    features: dataTableFeatures,
    data,
    columns: resolvedColumns,
    getRowId,
    getSubRows: resolvedGetSubRows,
    ...(manualFiltering ? { manualFiltering: true } : {}),
    ...(manualSorting ? { manualSorting: true } : {}),
    defaultColumn,
    enableColumnPinning,
    // Preserve v8 checkbox behavior; v9 enables Shift range selection by default.
    enableRowRangeSelection: false,
    ...(!paginationEnabled || manualPagination ? { manualPagination: true } : {}),
    ...(paginationEnabled && rowCount != null ? { rowCount } : {}),
    ...(expandableEnabled
      ? {
          enableExpanding: true,
          // Expanding often mutates row data (e.g. VendorActivityTable loads campaign
          // subRows after expand). v9 auto-resets expanded state whenever the core row
          // model recomputes from a new `data` reference, which immediately collapses
          // the row we just opened. DataTable owns expansion via controlled/uncontrolled
          // state, so disable the library auto-reset.
          autoResetExpanded: false,
        }
      : {}),
    ...(selectionEnabled
      ? {
          enableRowSelection: resolvedGetRowCanSelect ?? true,
          enableMultiRowSelection,
          enableSubRowSelection,
        }
      : {}),
    enableMultiSort: false,
    enableSortingRemoval: false,
    sortDescFirst: false,
    ...(paginationEnabled ? { autoResetPageIndex: !manualPagination } : {}),
    initialState,
    state,
    ...(paginationEnabled ? { onPaginationChange } : {}),
    ...(selectionEnabled ? { onRowSelectionChange } : {}),
    ...(expandableEnabled
      ? {
          onExpandedChange,
          ...(resolvedGetRowCanExpand != null ? { getRowCanExpand: resolvedGetRowCanExpand } : {}),
        }
      : {}),
    ...(onSortingChange ? { onSortingChange } : {}),
    ...(onColumnFiltersChange ? { onColumnFiltersChange } : {}),
    ...(resolvedOnColumnOrderChange ? { onColumnOrderChange: resolvedOnColumnOrderChange } : {}),
    ...(resolvedOnColumnVisibilityChange
      ? { onColumnVisibilityChange: resolvedOnColumnVisibilityChange }
      : {}),
  });
  const currentSorting = (sorting ?? table.state.sorting ?? []) as SortingState;

  const sortedColumnId = currentSorting[0]?.id;

  const rows = table.getRowModel().rows;
  const rowsById = table.getPreExpandedRowModel().rowsById;
  const footerGroups = table.getFooterGroups();
  // Iterate columns in `[start, center, end]` order so the colgroup and skeleton rows
  // line up with what headers (`getHeaderGroups`) and body cells (`row.getVisibleCells`)
  // produce. `table.getVisibleLeafColumns()` only honors `columnOrder`; it ignores
  // `columnPinning`, which would otherwise misalign `<col>` widths with their cells when
  // a pinned column is positioned anywhere other than its pinned edge in `columnOrder`.
  const visibleLeafColumns = [
    ...table.getStartVisibleLeafColumns(),
    ...table.getCenterVisibleLeafColumns(),
    ...table.getEndVisibleLeafColumns(),
  ];
  const visibleColumnWidths: Record<string, number> = {};
  visibleLeafColumns.forEach((column) => {
    visibleColumnWidths[column.id] = allocatedColumnWidths[column.id] ?? column.getSize();
  });
  const renderedTableWidth = Object.values(visibleColumnWidths).reduce(
    (sum, width) => sum + width,
    0,
  );
  const pinnedOffsets = computePinnedOffsets(table.state.columnPinning, visibleColumnWidths);
  const colSpan = Math.max(visibleLeafColumns.length, 1);
  const showSkeletonRows = loading && data.length === 0 && !error;
  const showLoadingOverlay = loading && !showSkeletonRows && !error;
  const hasFooter = footerGroups.some((footerGroup) =>
    footerGroup.headers.some(
      (header) => !header.isPlaceholder && header.column.columnDef.footer != null,
    ),
  );
  const showPingRightShadow = canScrollRight && !canScrollLeft;
  const showPingLeftShadow = canScrollLeft && !canScrollRight;

  return (
    <SpinnerOverlay isSpinning={showLoadingOverlay} className={className}>
      <div className="flex flex-col gap-2">
        <div
          ref={tableScrollWrapperRef}
          className={cn(
            'relative',
            'before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-4 before:w-7.5 before:transition-shadow',
            'after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-4 after:w-7.5 after:transition-shadow',
            showPingLeftShadow && 'before:[box-shadow:inset_10px_0_8px_-8px_rgba(5,5,5,0.06)]',
            showPingRightShadow && 'after:[box-shadow:inset_-10px_0_8px_-8px_rgba(5,5,5,0.06)]',
          )}
        >
          {showHeader && stickyHeader ? (
            <div
              ref={headerWrapperRef}
              data-slot="data-table-sticky-header"
              className="sticky top-0 z-30 no-scrollbar overflow-x-auto"
            >
              <table
                className={cn('w-full table-fixed caption-bottom text-xs', tableClassName)}
                style={{ width: `${renderedTableWidth}px` }}
              >
                <colgroup>
                  {visibleLeafColumns.map((column) => (
                    <col
                      key={column.id}
                      style={{
                        width: `${visibleColumnWidths[column.id]}px`,
                      }}
                    />
                  ))}
                </colgroup>
                <DataTableTableHead
                  headerGroups={table.getHeaderGroups()}
                  canScrollLeft={canScrollLeft}
                  canScrollRight={canScrollRight}
                  sorting={currentSorting}
                  pinnedOffsets={pinnedOffsets}
                />
              </table>
            </div>
          ) : null}
          <Table
            className={cn('table-fixed', tableClassName)}
            style={{ width: `${renderedTableWidth}px` }}
          >
            <colgroup>
              {visibleLeafColumns.map((column) => (
                <col
                  key={column.id}
                  style={{
                    width: `${visibleColumnWidths[column.id]}px`,
                  }}
                />
              ))}
            </colgroup>
            {showHeader && !stickyHeader ? (
              <DataTableTableHead
                headerGroups={table.getHeaderGroups()}
                canScrollLeft={canScrollLeft}
                canScrollRight={canScrollRight}
                sorting={currentSorting}
                pinnedOffsets={pinnedOffsets}
              />
            ) : null}
            <TableBody>
              {error ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={colSpan}
                    className="h-24 text-center text-gray-500 group-hover:bg-transparent hover:bg-transparent"
                  >
                    {errorMessage}
                  </TableCell>
                </TableRow>
              ) : showSkeletonRows ? (
                Array.from({ length: Math.max(1, loadingRowCount) }).map((_, rowIndex) => (
                  <TableRow
                    key={`loading-row-${rowIndex}`}
                    className="pointer-events-none hover:bg-transparent"
                  >
                    {visibleLeafColumns.map((column) => (
                      <TableCell
                        key={`${rowIndex}-${column.id}`}
                        className={cn(
                          size === 'small' ? 'h-auto px-2 py-1' : undefined,
                          getPinnedCellClassName(column, 'body', canScrollLeft, canScrollRight),
                          'group-hover:bg-transparent hover:bg-transparent',
                        )}
                        style={getPinnedCellStyles(column, pinnedOffsets)}
                      >
                        <Skeleton className="h-4 w-full rounded-xs" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length > 0 ? (
                /* eslint-disable react/refs -- first-paint sub-rows skip enter animation; the expansion re-render is already scheduled */
                rows.map((row) => {
                  const canExpand = row.getCanExpand();
                  const logicallyExpanded = resolvedExpandedRowIdSet.has(row.id);
                  const selected = resolvedSelectedRowIdSet.has(row.id);
                  const isNestedSubRow = subRowsExpandableMode && row.depth > 0;
                  const isCollapsingSubRow =
                    isNestedSubRow &&
                    rowHasAncestorInSet(row, collapsingExpandedRowIdSet, rowsById);
                  const isSubRowVisible = !isCollapsingSubRow;
                  const rowInteractionProps: HTMLAttributes<HTMLTableRowElement> =
                    onRow?.(row.original, row.index) ?? {};
                  const { className: onRowClassName, ...rowInteractionRest } = rowInteractionProps;

                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        className={cn(
                          'group',
                          'hover:bg-transparent',
                          isNestedSubRow && !isSubRowVisible && 'border-b-0',
                          rowCursor === 'pointer' && 'cursor-pointer',
                          rowCursor === 'auto' && 'cursor-auto',
                          onRowClassName,
                        )}
                        data-state={selected ? 'selected' : undefined}
                        aria-expanded={canExpand ? logicallyExpanded : undefined}
                        aria-selected={selectionEnabled ? selected : undefined}
                        {...rowInteractionRest}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <DataTableTableBodyCell
                            key={cell.id}
                            cell={cell}
                            canScrollLeft={canScrollLeft}
                            canScrollRight={canScrollRight}
                            sortedColumnId={sortedColumnId}
                            rowExpanded={canExpand && logicallyExpanded}
                            pinnedOffsets={pinnedOffsets}
                            animateVisibility={isNestedSubRow}
                            isVisible={isSubRowVisible}
                            animationDurationMs={expansionAnimationDurationMs}
                            size={size}
                            suppressEnterAnimation={!hasMountedRef.current}
                          />
                        ))}
                      </TableRow>
                      {detailExpandable != null && canExpand && (
                        <DataTableExpandedRow
                          colSpan={colSpan}
                          isExpanded={logicallyExpanded}
                          className={detailExpandable.expandedRowClassName}
                          durationMs={detailExpandable.durationMs}
                        >
                          {detailExpandable.expandedRowRender(
                            row.original,
                            row.index,
                            row.depth,
                            logicallyExpanded,
                          )}
                        </DataTableExpandedRow>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                /* eslint-enable react/refs */
                <TableRow className="bg-transparent!">
                  <TableCell colSpan={colSpan}>
                    <div className="pointer-events-none sticky left-1/2 flex w-max -translate-x-1/2 items-center text-xs text-gray-500">
                      {emptyMessage}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {!showSkeletonRows && !error && hasFooter && (
              <TableFooter>
                {footerGroups.map((footerGroup) => (
                  <TableRow key={footerGroup.id} className="group hover:bg-transparent">
                    {footerGroup.headers.map((header) => (
                      <DataTableTableFooterCell
                        key={header.id}
                        header={header}
                        canScrollLeft={canScrollLeft}
                        canScrollRight={canScrollRight}
                        sortedColumnId={sortedColumnId}
                        pinnedOffsets={pinnedOffsets}
                        size={size}
                      />
                    ))}
                  </TableRow>
                ))}
              </TableFooter>
            )}
          </Table>
        </div>
        {paginationEnabled || actionContent ? (
          <div className="flex gap-4.5 pt-4 pb-1">
            <div className="flex flex-1 flex-wrap">{!!actionContent && actionContent}</div>
            <DataTablePagination
              table={table}
              paginationState={resolvedPaginationState}
              rowCount={rowCount}
              loading={showSkeletonRows}
              error={error}
              paginationEnabled={paginationEnabled}
            />
          </div>
        ) : null}
      </div>
      {showVirtualScrollbar && (
        <div
          className="pointer-events-auto fixed bottom-0 z-40"
          style={{
            left: `${virtualScrollbarLeft}px`,
            width: `${virtualScrollbarWidth}px`,
          }}
        >
          <ScrollAreaPrimitive.Root data-slot="data-table-virtual-scrollbar" className="w-full">
            <ScrollAreaPrimitive.Viewport
              ref={virtualScrollbarViewportRef}
              onScroll={onVirtualScrollbarScroll}
              className="h-2.5 w-full"
            >
              <div
                className="h-px"
                style={{
                  width: `${virtualScrollbarContentWidth}px`,
                }}
              />
            </ScrollAreaPrimitive.Viewport>
            <ScrollBar orientation="horizontal" />
          </ScrollAreaPrimitive.Root>
        </div>
      )}
    </SpinnerOverlay>
  );
}

export default DataTable;
